/** @format */

import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { sectionRenderer } from "@theme/section-renderer";

class CodOrderModal {
  /**
   * @param {HTMLElement} modal
   */
  constructor(modal) {
    if (!(modal instanceof HTMLElement)) return;
    if (modal.dataset.codInitialized === "true") return;
    modal.dataset.codInitialized = "true";

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    this.modal = modal;
    this.sectionId = modal.dataset.sectionId;
    this.trigger = document.querySelector(
      `[data-cod-buy-now-trigger="${this.sectionId}"]`
    );
    // Trigger is optional - can be set later for cart drawer usage
    // if (!this.trigger) return;

    this.productForm = this.trigger?.closest("product-form-component") || null;
    this.overlay = modal.querySelector("[data-cod-overlay]");
    this.closeButtons = modal.querySelectorAll("[data-cod-close]");
    this.orderForm = modal.querySelector("[data-cod-order-form]");
    this.shippingInputs = modal.querySelectorAll(
      'input[name="cod_shipping_option"]'
    );
    this.cartItemsContainer = modal.querySelector("[data-cart-items]");
    this.summary = {
      subtotal: modal.querySelector("[data-summary-subtotal]"),
      discount: modal.querySelector("[data-summary-discount]"),
      shipping: modal.querySelector("[data-summary-shipping]"),
      total: modal.querySelector("[data-summary-total]"),
    };
    this.couponInput = modal.querySelector("[data-coupon-input]");
    this.couponApplyButton = modal.querySelector("[data-coupon-apply]");
    this.couponFeedback = modal.querySelector("[data-coupon-feedback]");
    this.discountsList = modal.querySelector("[data-active-discounts]");
    this.errorFeedback = modal.querySelector("[data-cod-error]");
    this.submitButton = modal.querySelector("[data-cod-submit]");
    this.moneyFormat = modal.dataset.moneyFormat || "{{amount}}";
    this.shopCurrency = modal.dataset.shopCurrency || "USD";

    // Hardcoded endpoint
    this.orderEndpoint = "https://saddhermoddhe.vercel.app/api/create-order";
    const confirmationSelector = `[data-cod-confirmation-modal="${this.sectionId}"]`;
    let confirmationModal = document.querySelector(confirmationSelector);
    if (!confirmationModal) {
      confirmationModal =
        modal.parentElement?.querySelector(confirmationSelector);
    }

    if (
      confirmationModal &&
      confirmationModal.parentElement !== document.body
    ) {
      document.body.appendChild(confirmationModal);
    }

    this.confirmationModal = confirmationModal || null;
    this.confirmationOverlay = this.confirmationModal?.querySelector(
      "[data-cod-confirm-overlay]"
    );
    this.confirmationCloseButtons = this.confirmationModal?.querySelectorAll(
      "[data-cod-confirm-close]"
    );

    this.awaitingModalOpen = false;
    this.isOpen = false;
    this.isConfirmationOpen = false;
    this.cart = null;
    this.activeDiscountCodes = [];

    this.handleTrigger = this.handleTrigger.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.close = this.close.bind(this);
    this.closeConfirmation = this.closeConfirmation.bind(this);
    this.handleOrderSubmit = this.handleOrderSubmit.bind(this);
    this.applyCoupon = this.applyCoupon.bind(this);
    this.handleDiscountListClick = this.handleDiscountListClick.bind(this);
    this.handleCartItemClick = this.handleCartItemClick.bind(this);
    this.showConfirmation = this.showConfirmation.bind(this);

    if (this.trigger) {
      this.trigger.addEventListener("click", this.handleTrigger);
    }
    this.overlay?.addEventListener("click", this.close);
    this.closeButtons.forEach((button) =>
      button.addEventListener("click", this.close)
    );
    this.orderForm?.addEventListener("submit", this.handleOrderSubmit);
    this.shippingInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.updateShippingPriceDisplay();
        this.updateSummary();
      });
    });
    // Format shipping price displays on initialization
    this.updateShippingPriceDisplay();
    this.couponApplyButton?.addEventListener("click", this.applyCoupon);
    this.couponInput?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.applyCoupon();
      }
    });
    this.discountsList?.addEventListener("click", this.handleDiscountListClick);
    this.cartItemsContainer?.addEventListener(
      "click",
      this.handleCartItemClick
    );
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.confirmationOverlay?.addEventListener("click", this.closeConfirmation);
    this.confirmationCloseButtons?.forEach((button) =>
      button.addEventListener("click", this.closeConfirmation)
    );
  }

  handleTrigger(event) {
    event.preventDefault();
    if (this.awaitingModalOpen || this.isOpen) return;
    this.hideMessages();

    // Add product to cart in background if on product page
    if (this.productForm) {
      const formElement = this.productForm.querySelector("form");
      if (formElement) {
        this.setTriggerLoading(true);
        this.awaitingModalOpen = true;

        // Open modal immediately
        this.open();

        // Submit form to add product to cart
        formElement.requestSubmit();

        // Fallback: if cart update event doesn't fire within 2 seconds, refresh cart anyway
        setTimeout(async () => {
          if (this.awaitingModalOpen && this.isOpen) {
            this.awaitingModalOpen = false;
            this.setTriggerLoading(false);
            await new Promise((resolve) => setTimeout(resolve, 300));
            await this.refreshCart();
          }
        }, 2000);

        // Cart will refresh when cart update event fires in handleCartUpdate
        return;
      }
    }

    // If no product form, open modal and refresh cart
    this.open();
  }

  async handleCartUpdate(event) {
    if (!event?.detail) return;

    // Check if this event is from the same modal to avoid infinite loops
    if (
      event.detail.sourceId === this.sectionId &&
      event.detail.data?.source === "cod-order-modal"
    ) {
      return;
    }

    if (this.awaitingModalOpen) {
      if (event.detail.data?.didError) {
        this.awaitingModalOpen = false;
        this.setTriggerLoading(false);
        this.showError("Unable to add product. Please try again.");
        // Close modal if there was an error
        if (this.isOpen) {
          this.close();
        }
        return;
      }

      this.awaitingModalOpen = false;
      this.setTriggerLoading(false);
      // Modal is already open, just refresh cart data
      // Wait longer to ensure cart is fully updated on server
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.refreshCart();
      return;
    }

    if (this.isOpen) {
      // Wait longer to ensure cart is fully updated on server
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.refreshCart();
    }
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return;

    if (this.isConfirmationOpen) {
      event.preventDefault();
      this.closeConfirmation();
      return;
    }

    if (this.isOpen) {
      event.preventDefault();
      this.close();
    }
  }

  async refreshCart(retries = 2) {
    try {
      // Add cache-busting query parameter to prevent stale data
      const cacheBuster = `?t=${Date.now()}`;
      const response = await fetch(`/cart.js${cacheBuster}`, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        cache: "no-store", // Explicitly disable caching
      });
      if (!response.ok) throw new Error("Unable to load cart");
      const cartData = await response.json();

      this.cart = cartData;
      this.activeDiscountCodes = Array.isArray(this.cart?.discount_codes)
        ? this.cart.discount_codes
            .filter((entry) => entry?.code)
            .map((entry) => entry.code.toUpperCase())
        : [];
      this.renderCartItems();
      this.renderDiscounts();
      this.updateSummary();
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return this.refreshCart(retries - 1);
      }
      this.showError("Unable to load cart contents. Please refresh the page.");
    }
  }

  renderCartItems() {
    if (!this.cartItemsContainer) return;
    const items = Array.isArray(this.cart?.items) ? this.cart.items : [];

    if (items.length === 0) {
      this.cartItemsContainer.innerHTML = `<li class="cod-cart-list__empty">${
        window.Theme?.translations?.your_cart_is_empty || "Your cart is empty"
      }</li>`;
      return;
    }

    const listMarkup = items
      .map((item) => {
        const imageMarkup = item.image
          ? `<img src="${item.image}" alt="${item.product_title}" class="cod-cart-item__media" loading="lazy">`
          : `<div class="cod-cart-item__media cod-cart-item__media--placeholder"></div>`;
        const variantTitle =
          item.variant_title && item.variant_title !== "Default Title"
            ? ` • ${item.variant_title}`
            : "";

        return `
          <li class="cod-cart-item">
            ${imageMarkup}
            <div class="cod-cart-item__content">
              <p class="cod-cart-item__title">${item.product_title}</p>
              <p class="cod-cart-item__meta">
                ${item.quantity} × ${this.formatCurrency(
          item.final_price
        )}${variantTitle}
              </p>
            </div>
            <strong>${this.formatCurrency(item.final_line_price)}</strong>
            <button
              type="button"
              class="cod-cart-item__remove"
              aria-label="Remove ${item.product_title}"
              data-remove-line="${item.key}"
            >
              ×
            </button>
          </li>
        `;
      })
      .join("");

    this.cartItemsContainer.innerHTML = listMarkup;
  }

  renderDiscounts() {
    if (!this.discountsList) return;
    const codes = this.getDiscountCodeList();

    if (!codes.length) {
      this.discountsList.innerHTML = "";
      return;
    }

    this.discountsList.innerHTML = codes
      .map(
        (code) => `
        <li data-discount-code="${code}">
          ${code}
          <button type="button" aria-label="Remove ${code}" data-remove-discount="${code}">×</button>
        </li>
      `
      )
      .join("");
  }

  updateSummary() {
    if (!this.summary || !this.cart) return;
    const subtotal = Number(this.cart.items_subtotal_price) || 0;
    const discount = Number(this.cart.total_discount) || 0;
    const shipping = this.getSelectedShippingCents();
    const total = Math.max(subtotal - discount + shipping, 0);

    if (this.summary.subtotal)
      this.summary.subtotal.textContent = this.formatCurrency(subtotal);
    if (this.summary.discount)
      this.summary.discount.textContent = this.formatCurrency(discount);
    if (this.summary.shipping)
      this.summary.shipping.textContent = this.formatCurrency(shipping);
    if (this.summary.total)
      this.summary.total.textContent = this.formatCurrency(total);
  }

  open() {
    // Close cart drawer if it's open
    const cartDrawer = document.querySelector("cart-drawer-component");
    if (cartDrawer && typeof cartDrawer.close === "function") {
      cartDrawer.close();
    }

    this.isOpen = true;
    // Remove any inline display:none style that might be preventing the modal from showing
    if (this.modal.style.display === "none") {
      this.modal.style.display = "";
    }
    this.modal.classList.add("is-visible");
    document.body.classList.add("cod-modal-open");
    this.modal.setAttribute("aria-hidden", "false");

    // Refresh cart data when opening modal (only if not awaiting cart update)
    if (!this.awaitingModalOpen) {
      this.refreshCart().then(() => {
        // Focus first field after cart is loaded
        const firstField = this.orderForm?.querySelector("input, textarea");
        if (firstField instanceof HTMLElement) {
          firstField.focus();
        }
      });
    } else {
      // If awaiting cart update, focus will happen after cart refresh in handleCartUpdate
      const firstField = this.orderForm?.querySelector("input, textarea");
      if (firstField instanceof HTMLElement) {
        firstField.focus();
      }
    }
  }

  close() {
    this.isOpen = false;
    this.modal.classList.remove("is-visible");
    document.body.classList.remove("cod-modal-open");
    this.modal.setAttribute("aria-hidden", "true");
  }

  handleOrderSubmit(event) {
    event.preventDefault();
    if (!this.orderForm) return;
    if (!this.cart || !this.cart.items?.length) {
      this.showError(
        "Please add a product to your cart before placing the order."
      );
      return;
    }

    this.toggleSubmitLoading(true);
    this.hideMessages();

    const formData = new FormData(this.orderForm);
    const customer = {
      name: formData.get("cod_name")?.toString().trim() || "",
      phone: formData.get("cod_phone")?.toString().trim() || "",
      address: formData.get("cod_address")?.toString().trim() || "",
    };

    if (!customer.name || !customer.phone || !customer.address) {
      this.toggleSubmitLoading(false);
      this.showError("Please complete all required fields.");
      return;
    }

    const shippingInput = this.orderForm.querySelector(
      'input[name="cod_shipping_option"]:checked'
    );
    const shippingCents = this.getSelectedShippingCents();
    // Send shipping amount in base currency (not cents) to backend
    // Backend will handle conversion if needed
    const shipping = {
      label: shippingInput?.value || "Standard shipping",
      amount: shippingCents / 100, // Convert cents to base currency for submission
    };

    // Calculate totals in cents first
    const subtotalCents = Number(this.cart.items_subtotal_price) || 0;
    const discountCents = Number(this.cart.total_discount) || 0;
    const totalCents = Math.max(
      subtotalCents - discountCents + shippingCents,
      0
    );

    const payload = {
      order_id: `COD-${Date.now()}`,
      customer,
      shipping,
      coupon_codes: this.getDiscountCodeList(),
      note: formData.get("cod_order_note")?.toString().trim() || "",
      cart: this.cart,
      totals: {
        // Convert all totals to base currency for backend consistency
        subtotal: subtotalCents / 100,
        discount: discountCents / 100,
        shipping: shippingCents / 100,
        total: totalCents / 100,
      },
    };

    this.sendToEndpoint(payload)
      .then(async () => {
        // Save order to localStorage as backup
        try {
          const orders = JSON.parse(localStorage.getItem("cod_orders") || "[]");
          orders.push({
            ...payload,
            timestamp: new Date().toISOString(),
            orderId: `COD-${Date.now()}`,
          });
          // Keep only last 50 orders
          const recentOrders = orders.slice(-50);
          localStorage.setItem("cod_orders", JSON.stringify(recentOrders));
        } catch (error) {
          // Failed to save to localStorage
        }

        // Clear cart after successful order submission
        await this.clearCart();

        this.resetOrderForm();
        this.showConfirmation();
      })
      .catch((error) => {
        this.showError(
          error?.message || "Unable to create order. Please try again."
        );
      })
      .finally(() => {
        this.toggleSubmitLoading(false);
      });
  }

  async sendToEndpoint(payload) {
    try {
      const response = await fetch(this.orderEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        throw new Error(
          `Order request failed (${response.status}). Please try again.`
        );
      }

      const responseData = await response.json().catch(() => ({}));
      return responseData;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Unable to connect to order endpoint. Please check your internet connection and try again."
        );
      }
      throw error;
    }
  }

  async applyCoupon() {
    if (!this.couponInput || !this.couponApplyButton) return;
    const code = this.couponInput.value.trim();
    if (!code) return;

    const codeUpper = code.toUpperCase();
    const existingCodes = this.getDiscountCodeList();
    if (existingCodes.includes(codeUpper)) {
      this.setCouponFeedback("Coupon already applied.", false);
      return;
    }

    this.setCouponFeedback("", false);
    this.couponApplyButton.disabled = true;

    try {
      const payload = {
        discount: [...existingCodes, codeUpper].join(","),
      };

      // Add cache-busting query parameter to prevent stale data
      const cacheBuster = Theme.routes.cart_update_url.includes("?")
        ? "&"
        : "?";
      const url = `${
        Theme.routes.cart_update_url
      }${cacheBuster}t=${Date.now()}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        cache: "no-store", // Explicitly disable caching
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      const appliedCode = data.discount_codes?.find(
        (entry) => entry.code?.toUpperCase() === codeUpper
      );

      if (!appliedCode || appliedCode.applicable === false) {
        this.setCouponFeedback(
          window.Theme?.translations?.discount_code_error ||
            "Coupon could not be applied.",
          true
        );
      } else {
        this.setCouponFeedback(
          window.Theme?.translations?.discounts || "Coupon applied!",
          false
        );
      }

      this.couponInput.value = "";

      // Wait longer to ensure cart is fully updated on server
      await new Promise((resolve) => setTimeout(resolve, 300));

      await this.refreshCart();

      // Dispatch cart update event to notify other components
      if (this.cart) {
        const itemCount = Array.isArray(this.cart.items)
          ? this.cart.items.length
          : 0;
        document.dispatchEvent(
          new CartUpdateEvent(this.cart, this.sectionId || "cod-order-modal", {
            itemCount,
            source: "cod-order-modal",
          })
        );
      }
    } catch (error) {
      this.setCouponFeedback("Unable to apply coupon. Please try again.", true);
    } finally {
      this.couponApplyButton.disabled = false;
    }
  }

  async handleDiscountListClick(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const button = event.target.closest("[data-remove-discount]");
    if (!button) return;
    const code = button.dataset.removeDiscount;
    const remainingCodes = this.getDiscountCodeList().filter(
      (existing) => existing !== code
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: remainingCodes.join(",") }),
      });
      if (!response.ok) throw new Error();
      await response.json();

      // Wait longer to ensure cart is fully updated on server
      await new Promise((resolve) => setTimeout(resolve, 300));

      await this.refreshCart();

      // Dispatch cart update event to notify other components
      if (this.cart) {
        const itemCount = Array.isArray(this.cart.items)
          ? this.cart.items.length
          : 0;
        document.dispatchEvent(
          new CartUpdateEvent(this.cart, this.sectionId || "cod-order-modal", {
            itemCount,
            source: "cod-order-modal",
          })
        );
      }
    } catch (error) {
      this.setCouponFeedback(
        "Unable to remove coupon. Please try again.",
        true
      );
    }
  }

  async handleCartItemClick(event) {
    const target = event.target.closest("[data-remove-line]");
    if (!target) return;
    event.preventDefault();
    const lineKey = target.dataset.removeLine;
    if (!lineKey) return;

    // Find the cart item element
    const cartItem = target.closest(".cod-cart-item");
    if (!cartItem) return;

    // Store original state for potential rollback
    const originalCart = this.cart
      ? JSON.parse(JSON.stringify(this.cart))
      : null;

    // Disable button and show loading state
    target.disabled = true;
    target.textContent = "…";

    // Animate removal for better UX
    cartItem.style.transition = "opacity 0.2s, transform 0.2s";
    cartItem.style.opacity = "0";
    cartItem.style.transform = "translateX(-20px)";

    // Wait for animation to complete, then remove from cart and update UI
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Remove the item from the cart items array optimistically
    if (this.cart && Array.isArray(this.cart.items)) {
      this.cart.items = this.cart.items.filter((item) => item.key !== lineKey);

      // Recalculate totals optimistically
      this.cart.items_subtotal_price = this.cart.items.reduce(
        (sum, item) => sum + (item.final_line_price || 0),
        0
      );

      // Update UI immediately
      this.renderCartItems();
      this.updateSummary();
    }

    try {
      // Make the server request
      const removeResponse = await this.removeCartItem(lineKey);

      // Wait longer to ensure cart is fully updated on server (especially important in production)
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Refresh cart data to sync with server (this will update the UI again)
      // Use a fresh timestamp to ensure we get the latest cart data
      await this.refreshCart();

      // Dispatch cart update event to notify other components
      if (this.cart) {
        const itemCount = Array.isArray(this.cart.items)
          ? this.cart.items.length
          : 0;
        document.dispatchEvent(
          new CartUpdateEvent(this.cart, this.sectionId || "cod-order-modal", {
            itemCount,
            source: "cod-order-modal",
          })
        );
      }
    } catch (error) {
      // Rollback optimistic update on error
      if (originalCart && this.cart) {
        this.cart = originalCart;
        this.renderCartItems();
        this.updateSummary();
      }
      this.showError("Unable to remove item. Please try again.");
    }
  }

  hideMessages() {
    this.errorFeedback?.classList.add("hidden");
    if (this.couponFeedback) this.couponFeedback.textContent = "";
  }

  showError(message) {
    if (this.errorFeedback) {
      this.errorFeedback.textContent = message;
      this.errorFeedback.classList.remove("hidden");
    }
  }

  setCouponFeedback(message, isError) {
    if (!this.couponFeedback) return;
    this.couponFeedback.textContent = message;
    this.couponFeedback.style.color = isError ? "#c22727" : "inherit";
  }

  setTriggerLoading(isLoading) {
    if (this.trigger) {
      this.trigger.classList.toggle("is-loading", isLoading);
      this.trigger.disabled = isLoading;
    }
  }

  async removeCartItem(lineKey) {
    // Add cache-busting query parameter to prevent stale data
    const cacheBuster = `?t=${Date.now()}`;
    const response = await fetch(`/cart/change.js${cacheBuster}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
      cache: "no-store", // Explicitly disable caching
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      throw new Error(`Failed to remove item from cart: ${errorText}`);
    }

    return await response.json();
  }

  toggleSubmitLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.classList.toggle("is-loading", isLoading);
    this.submitButton.disabled = isLoading;
  }

  async clearCart() {
    try {
      // Clear the cart with cache-busting
      const clearCacheBuster = `?t=${Date.now()}`;
      await fetch(`/cart/clear.js${clearCacheBuster}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        cache: "no-store", // Explicitly disable caching
      });

      // Fetch the updated empty cart with cache-busting
      const cartCacheBuster = `?t=${Date.now()}`;
      const cartResponse = await fetch(`/cart.js${cartCacheBuster}`, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        cache: "no-store", // Explicitly disable caching
      });
      if (!cartResponse.ok) throw new Error("Failed to fetch cart");
      const emptyCart = await cartResponse.json();

      // Update internal cart state
      this.cart = emptyCart;
      this.activeDiscountCodes = [];
      this.renderCartItems();
      this.renderDiscounts();
      this.updateSummary();

      // Find all cart drawer sections and refresh them
      const cartDrawerSections = Array.from(
        document.querySelectorAll("cart-items-component")
      )
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);

      // Refresh cart drawer sections if they exist
      if (cartDrawerSections.length > 0) {
        const sectionPromises = cartDrawerSections.map((sectionId) =>
          sectionRenderer
            .renderSection(sectionId, { cache: false })
            .catch(() => {
              // Failed to refresh section
            })
        );
        await Promise.all(sectionPromises);
      }

      // Dispatch cart update event to update cart icon and other components
      // Use itemCount: 0 and NOT from product-form so it sets to 0, not adds
      document.dispatchEvent(
        new CartUpdateEvent(emptyCart, this.sectionId || "cod-order-modal", {
          itemCount: 0,
          source: "cod-order-modal",
        })
      );
    } catch (error) {
      // Failed to clear cart
    }
  }

  resetOrderForm() {
    if (!this.orderForm) return;
    this.orderForm.reset();
    if (this.shippingInputs.length) {
      this.shippingInputs.forEach((input, index) => {
        input.checked = index === 0;
      });
      this.shippingInputs[0]?.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    } else {
      this.updateSummary();
    }
  }

  showConfirmation() {
    if (!this.confirmationModal) {
      this.close();
      return;
    }

    this.close();
    this.isConfirmationOpen = true;
    this.confirmationModal.classList.add("is-visible");
    this.confirmationModal.setAttribute("aria-hidden", "false");
    document.body.classList.add("cod-confirmation-open");
    const focusTarget =
      this.confirmationModal.querySelector("[data-cod-confirm-close]") ||
      this.confirmationModal;
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }
  }

  closeConfirmation() {
    if (!this.confirmationModal) return;
    this.isConfirmationOpen = false;
    this.confirmationModal.classList.remove("is-visible");
    this.confirmationModal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cod-confirmation-open");
  }

  updateShippingPriceDisplay() {
    // Ensure all shipping price displays are formatted correctly
    const shippingPriceDisplays = this.modal?.querySelectorAll(
      "[data-shipping-display]"
    );
    if (shippingPriceDisplays) {
      shippingPriceDisplays.forEach((display) => {
        const cents = Number(display.dataset.shippingDisplay) || 0;
        display.textContent = this.formatCurrency(cents);
      });
    }
  }

  getSelectedShippingCents() {
    const selected = this.orderForm?.querySelector(
      'input[name="cod_shipping_option"]:checked'
    );
    // Return value in cents (already stored as cents in data-shipping-price)
    return selected ? Number(selected.dataset.shippingPrice) || 0 : 0;
  }

  getDiscountCodeList() {
    return Array.from(this.activeDiscountCodes || []);
  }

  formatCurrency(value) {
    const cents = Number(value) || 0;
    if (window.Shopify?.formatMoney) {
      try {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      } catch {
        // fall-through
      }
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: this.shopCurrency,
      minimumFractionDigits: 0,
    }).format(cents / 100);
  }
}

// Store modal instances globally so they can be accessed from cart drawer
const modalInstances = new Map();

function init(scope = document) {
  // Initialize all modals, prioritizing global modal
  const allModals = Array.from(scope.querySelectorAll("[data-cod-modal]"));
  // Sort to prioritize global modal
  const sortedModals = allModals.sort((a, b) => {
    const aId = a.dataset.sectionId;
    const bId = b.dataset.sectionId;
    if (aId === "global") return -1;
    if (bId === "global") return 1;
    return 0;
  });

  sortedModals.forEach((modal) => {
    const sectionId = modal.dataset.sectionId;
    if (sectionId && !modalInstances.has(sectionId)) {
      try {
        const instance = new CodOrderModal(modal);
        if (instance && instance.modal) {
          modalInstances.set(sectionId, instance);
        }
      } catch (error) {
        // Error initializing modal
      }
    }
  });

  // Handle cart drawer buttons - find and attach to existing modal
  scope
    .querySelectorAll('[data-cod-buy-now-trigger="cart-drawer"]')
    .forEach((button) => {
      if (button.dataset.codAttached === "true") return;
      button.dataset.codAttached = "true";

      button.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();

        // Find any existing modal instance, prioritizing global modal
        let existingInstance =
          modalInstances.get("global") ||
          Array.from(modalInstances.values())[0];

        // If no instance exists, try to find and initialize a modal HTML element
        if (!existingInstance) {
          // Prioritize global modal first, then any other modal
          let modalElement =
            document.querySelector(
              '[data-cod-modal][data-section-id="global"]'
            ) || document.querySelector("[data-cod-modal]");

          // If no modal HTML exists, wait a bit for dynamic content to load
          if (!modalElement) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            modalElement =
              document.querySelector(
                '[data-cod-modal][data-section-id="global"]'
              ) || document.querySelector("[data-cod-modal]");
          }

          if (modalElement) {
            const sectionId = modalElement.dataset.sectionId;
            if (sectionId) {
              // Check if already initialized
              if (modalInstances.has(sectionId)) {
                existingInstance = modalInstances.get(sectionId);
              } else {
                // Initialize new instance
                try {
                  existingInstance = new CodOrderModal(modalElement);
                  if (existingInstance && existingInstance.modal) {
                    modalInstances.set(sectionId, existingInstance);
                  } else {
                    existingInstance = null;
                  }
                } catch (error) {
                  existingInstance = null;
                }
              }
            }
          }
        }

        if (existingInstance && existingInstance.modal) {
          // Temporarily set this button as the trigger
          const originalTrigger = existingInstance.trigger;
          existingInstance.trigger = button;
          existingInstance.productForm = null; // No product form in cart drawer

          // Refresh cart data before opening (important for draft order creation)
          await existingInstance.refreshCart();

          existingInstance.handleTrigger(event);
          // Restore original trigger after a delay
          setTimeout(() => {
            if (existingInstance) {
              existingInstance.trigger = originalTrigger;
            }
          }, 100);
        }
      });
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    init();
  });
} else {
  init();
}

document.addEventListener("shopify:section:load", (event) => {
  init(event.target);
});

// Listen for cart drawer opening to re-initialize buttons
// Try multiple event names that might be used
const cartDrawerEvents = ["cart:open", "dialog:open", "cart-drawer:open"];

cartDrawerEvents.forEach((eventName) => {
  document.addEventListener(eventName, (event) => {
    // Check if this is a cart drawer event
    const isCartDrawer =
      event.target?.classList?.contains("cart-drawer__dialog") ||
      event.target?.closest?.("cart-drawer-component") ||
      document.querySelector("cart-drawer-component dialog[open]");

    if (isCartDrawer || eventName === "cart:open") {
      // Re-initialize cart drawer buttons when drawer opens
      setTimeout(() => {
        const cartDrawer = document.querySelector("cart-drawer-component");
        if (cartDrawer) {
          // Remove old attachments to allow re-initialization
          cartDrawer
            .querySelectorAll('[data-cod-buy-now-trigger="cart-drawer"]')
            .forEach((button) => {
              button.dataset.codAttached = "false";
            });
          init(cartDrawer);
        }
      }, 150);
    }
  });
});

// Also use MutationObserver to watch for cart drawer opening
function setupCartDrawerObserver() {
  if (!document.body) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", setupCartDrawerObserver);
    } else {
      setTimeout(setupCartDrawerObserver, 100);
    }
    return;
  }

  const cartDrawerObserver = new MutationObserver((mutations) => {
    const cartDrawer = document.querySelector("cart-drawer-component");
    const cartDrawerDialog = cartDrawer?.querySelector("dialog[open]");

    if (cartDrawerDialog) {
      // Cart drawer is open, re-initialize buttons
      setTimeout(() => {
        cartDrawer
          .querySelectorAll('[data-cod-buy-now-trigger="cart-drawer"]')
          .forEach((button) => {
            if (button.dataset.codAttached !== "true") {
              button.dataset.codAttached = "false";
            }
          });
        init(cartDrawer);
      }, 100);
    }
  });

  cartDrawerObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["open"],
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setupCartDrawerObserver);
} else {
  setupCartDrawerObserver();
}
